import './map.css';

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { isNullOrUndefinedOrBlank } from '../../util/isNullOrUndefinedOrBlank';
import { ICoord } from '../../models/coord.model';
import { IListings } from '../../models/listings.model';
import { getOverlayPixelPositionOffset, GOOGLE_MAPS_ID } from '../../util/google.constants';
import DrawingMapControls from './drawing-map-controls';
import { MAP_SEARCH_DEFAULT_ZOOM } from '../../util/listing-search.constants';
import { IListing } from '../../models/listing.model';
import { GDouglasPeucker } from './GDouglasPeucker.js';
import MapResults from './map-results';
import { Helmet } from 'react-helmet-async';

export interface MapContainerProps {
  listings: IListings;
  mapCenterCoord: ICoord;
  mapFitBounds: google.maps.LatLngBounds | null;
  activePreviewListingId: string;
  isMapCenterInitialized: boolean;
  setGeoPolySearch(value: ICoord[]): void;
  setActivePreviewListingId(value: string): void;
  setMapCenterCoord(value: ICoord): void;
  openListing(listing: IListing): void;
  updatePrevSearchUrl(): void;
}

export const MapContainer: React.FC<MapContainerProps> = props => {
  const [isDragging, setIsDragging] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const boundaries = useRef<google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [, setForceUpdate] = useState(Date.now());
  const [mapZoom, setMapZoom] = useState(MAP_SEARCH_DEFAULT_ZOOM);
  const centerCoord = useRef<ICoord>(props.mapCenterCoord);
  const fitBounds = useRef<google.maps.LatLngBounds | null>(props.mapFitBounds);
  const listingMarkerTimer = useRef<NodeJS.Timeout>(setTimeout(() => null, 100));
  const hasMapMoved = useRef<boolean>(false);
  const initializing = useRef<boolean>(true);

  const enableHasMapMoved = () => {
    if (!initializing.current && !hasMapMoved.current && polygonRef.current === null) {
      hasMapMoved.current = true;
    }
  };

  const getOverlayPixelPositionOffsetWrapper = (width: number, height: number, coords: ICoord | undefined) =>
    getOverlayPixelPositionOffset(width, height, coords, mapRef.current!);

  const setRootCoord = useCallback(function () {
    polygonRef.current === null && centerCoord.current.lat !== 0 &&
      props.setMapCenterCoord({ lat: centerCoord.current.lat, lng: centerCoord.current.lng });
  }, [props]);

  const onCenterChanged = useCallback(function () {
    if (mapRef.current !== null
      && mapRef.current !== undefined
      && mapRef.current.getCenter() !== null
      && mapRef.current.getCenter() !== undefined
      && (mapRef.current.getCenter()!.lng() !== centerCoord.current.lng && mapRef.current.getCenter()!.lat() !== centerCoord.current.lat)) {
      centerCoord.current = { lat: mapRef.current.getCenter()!.lat(), lng: mapRef.current.getCenter()!.lng() };
      enableHasMapMoved();
    }
  }, []);

  const onMouseOverListingMarker = useCallback(function (listingId: string) {
    clearTimeout(listingMarkerTimer.current);
    props.setActivePreviewListingId(listingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseOutListingMarker = useCallback(function () {
    listingMarkerTimer.current = setTimeout(() => props.setActivePreviewListingId(''), 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateGeoSearchToMapBoundaries = useCallback(function (force: boolean = false) {
    const northEast = mapRef.current!.getBounds()!.getNorthEast();
    const southWest = mapRef.current!.getBounds()!.getSouthWest();
    if (hasMapMoved.current) {
      // do nothing
    } else if (force || !mapRef.current!.getBounds()?.equals(boundaries.current)) {
      // force or check if new boundaries are different that the existing so update
      boundaries.current = mapRef.current!.getBounds()!;
      props.setGeoPolySearch([
        { lat: northEast.lat(), lng: northEast.lng() },
        { lat: southWest.lat(), lng: northEast.lng() },
        { lat: southWest.lat(), lng: southWest.lng() },
        { lat: northEast.lat(), lng: southWest.lng() },
        { lat: northEast.lat(), lng: northEast.lng() }
      ]);
    }
  }, [props]);

  const clearHasMapMoved = (value: boolean) => {
    hasMapMoved.current = value;
    updateGeoSearchToMapBoundaries(true);
  };

  const removeBoundary = useCallback(function () {
    polygonRef.current !== null &&
      polygonRef.current.setMap(null);
    polygonRef.current = null;
    setRootCoord();
    updateGeoSearchToMapBoundaries(true);
  }, [setRootCoord, updateGeoSearchToMapBoundaries]);

  const checkBoundsChanged = useCallback(function () {
    if (polygonRef.current !== null || mapRef.current === null || isDrawing || isDragging) {
      return;
    } else if (!isNullOrUndefinedOrBlank(mapRef.current.getBounds())) {
      if (initializing.current) {
        updateGeoSearchToMapBoundaries();
        initializing.current = false;
      } else if (!hasMapMoved.current) {
        enableHasMapMoved();
        setForceUpdate(Date.now());
      }
    }
  }, [isDragging, isDrawing, updateGeoSearchToMapBoundaries]);

  const polygonToGeo = useCallback(function () {
    if (polygonRef.current !== null) {
      const paths = polygonRef.current.getPath();
      const boundary: ICoord[] = [];
      for (let i = 0; i < paths.getLength(); i++) {
        boundary.push({ lat: paths.getAt(i).lat(), lng: paths.getAt(i).lng() });
      }
      props.setGeoPolySearch(boundary);
    }
  }, [props]);

  const onZoomChanged = useCallback(function () {
    if (mapRef.current !== null && mapZoom !== mapRef.current.getZoom()) {
      enableHasMapMoved();
      setMapZoom(mapRef.current.getZoom()!);
    }
  }, [mapZoom]);

  const applyDrawing = useCallback(function () {
    polygonToGeo();
    setIsDrawing(false);
    hasMapMoved.current = false;
  }, [polygonToGeo]);

  const cancelDrawing = useCallback(function () {
    polygonRef.current !== null &&
      polygonRef.current.setMap(null);
    polylineRef.current !== null &&
      polylineRef.current.setMap(null);
    polylineRef.current = null;
    polygonRef.current = null;
    setIsDrawing(false);
  }, []);

  const drawFreeHand = useCallback(function () {
    //the polyline
    polylineRef.current = new google.maps.Polyline({ map: mapRef.current, clickable: false });

    //move-listener
    var move = google.maps.event.addListener(mapRef.current!, 'mousemove', function (e: google.maps.MapMouseEvent) {
      e.latLng !== null &&
        polylineRef.current!.getPath().push(e.latLng);
    });

    //mouseup-listener
    google.maps.event.addListenerOnce(mapRef.current!, 'mouseup', function (e: google.maps.MapMouseEvent) {
      google.maps.event.removeListener(move);
      const path = polylineRef.current!.getPath();
      polylineRef.current!.setMap(null);
      polylineRef.current = null;
      const theArrayofLatLng = path.getArray();
      const arrayforPolygontoUse = GDouglasPeucker(theArrayofLatLng, 50);
      const polyOptions = {
        map: mapRef.current,
        fillColor: '#000000',
        fillOpacity: 0.2,
        strokeColor: '#000000',
        strokeWeight: 2,
        clickable: false,
        zIndex: 1,
        path: arrayforPolygontoUse,
        editable: false
      }
      polygonRef.current !== null && polygonRef.current.setMap(null);
      polygonRef.current = new google.maps.Polygon(polyOptions);
      google.maps.event.clearListeners(mapRef.current!.getDiv(), 'mousedown');
      setForceUpdate(Date.now());
    });
  }, []);

  const setFitBounds = function (bounds: google.maps.LatLngBounds) {
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds);
    }
  };

  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
  }, []);

  const onIdle = useCallback(function () {
    if (fitBounds.current) {
      setFitBounds(fitBounds.current);
      fitBounds.current = null;
    } else {
      checkBoundsChanged();
      setRootCoord();
    }
    props.updatePrevSearchUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkBoundsChanged, setRootCoord, ]);

  const mapResults = useMemo(() => <MapResults
    listings={props.listings}
    activePreviewListingId={props.activePreviewListingId}
    zoomLevel={mapZoom}
    setActivePreviewListingId={props.setActivePreviewListingId}
    openListing={props.openListing}
    getOverlayPixelPositionOffsetWrapper={getOverlayPixelPositionOffsetWrapper}
    onMouseOverListingMarker={onMouseOverListingMarker}
    onMouseOutListingMarker={onMouseOutListingMarker}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  />, [props.listings, props.activePreviewListingId, mapZoom]);

  const loadingNotice = useMemo(() => (
    (props.listings.loading || !props.isMapCenterInitialized)
      ? <div className="map-results-loading">Loading ...</div>
      : null
  ), [props.isMapCenterInitialized, props.listings.loading]);

  useEffect(() => {
    if (centerCoord.current.lat !== props.mapCenterCoord.lat || centerCoord.current.lng !== props.mapCenterCoord.lng) {
      centerCoord.current = props.mapCenterCoord;
      setForceUpdate(Date.now());
    }
  }, [props.mapCenterCoord]);

  useEffect(() => {
    if (isDrawing) {
      //mousedown-listener
      google.maps.event.addListener(mapRef.current!, 'mousedown', function (e: google.maps.MapMouseEvent) {
        drawFreeHand();
      });
    }
    return () => {
      google.maps.event.clearListeners(mapRef.current!, 'mousedown');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing]);

  return (
    <div className="map-container">
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      {props.isMapCenterInitialized &&
        <GoogleMap
          id={GOOGLE_MAPS_ID}
          onLoad={onLoad}
          mapContainerStyle={{ height: '100%', width: '100%' }}
          onIdle={onIdle}
          zoom={mapZoom}
          onZoomChanged={onZoomChanged}
          center={centerCoord.current}
          onCenterChanged={onCenterChanged}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          clickableIcons={false}
          options={{
            streetViewControl: false,
            scaleControl: false,
            fullscreenControl: false,
            scrollwheel: false,
            draggable: !isDrawing,
            styles: [
              {
                featureType: "poi.business",
                elementType: "labels",
                stylers: [{
                  visibility: "off"
                }],
              }
            ],
            gestureHandling: "greedy",
            disableDoubleClickZoom: !isDrawing,
            mapTypeControl: false,
            mapTypeControlOptions: {
              position: google.maps.ControlPosition.TOP_LEFT,
              style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
            },
            mapTypeId: google.maps.MapTypeId.ROADMAP
          }}
        >
          {hasMapMoved.current &&
            <div className="search-lock-button-div">
              <button onClick={() => clearHasMapMoved(false)}>Search This Area</button>
            </div>
          }
          <DrawingMapControls
            isDrawing={isDrawing}
            haveShape={polygonRef.current !== null}
            enableDrawing={() => setIsDrawing(true)}
            applyDrawing={applyDrawing}
            cancelDrawing={cancelDrawing}
            removeBoundary={removeBoundary}
          />
          {!isDrawing && props.listings.properties !== undefined &&
            mapResults
          }
        </GoogleMap>
      }
      {loadingNotice}
    </div>
  );
}

export default MapContainer;
